import * as XLSX from "xlsx";

export const containsSuspiciousContent = (fileData) => {
  const workbook = XLSX.read(fileData, { type: "array" });

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const parsedData = XLSX.utils.sheet_to_json(sheet);

    // Check for SQL injection patterns in the parsed data
    for (const row of parsedData) {
      for (const key in row) {
        const cellValue = row[key].toString();
        const sqlInjectionPatterns =
          /(select\s*\*\s*from)|(sleep\s*\(\s*\d+\s*\))|(insert\s+into\s+\w+\s*\([^)]*\)\s*values\s*\([^)]*\))/i;

        if (sqlInjectionPatterns.test(cellValue)) {
          return true;
        }
      }
    }
  }

  return false;
};

export const readExcelFile = async (file) => {
  try {
    const bufferArray = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });

    const wb = XLSX.read(bufferArray, { type: "buffer" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(ws);
    const headers = XLSX.utils.sheet_to_json(ws, { header: 1 })[0];

    // Check for suspicious content
    if (containsSuspiciousContent(bufferArray)) {
      return {
        data: null,
        headers: null,
        error: "File contains suspicious content. Upload Aborted",
      };
    }

    return { data, headers, error: null };
  } catch (error) {
    return {
      data: null,
      headers: null,
      error: "An error occurred, aborting file upload.",
    };
  }
};

// Image file reader function
export const readImageFile = async (file) => {
  try {
    const imageData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });

    // Check if the file is an image
    if (!isValidImage(imageData)) {
      return {
        data: null,
        error: "Invalid image file. Upload aborted.",
      };
    }

    return { data: file, error: null };
  } catch (error) {
    return {
      data: null,
      error: "An error occurred, aborting file upload.",
    };
  }
};

// Function to check if the file is a valid image
export const isValidImage = (arrayBuffer) => {
  const arr = new Uint8Array(arrayBuffer).subarray(0, 4);
  let header = "";
  for (let i = 0; i < arr.length; i++) {
    header += arr[i].toString(16);
  }

  // Check the file signature
  switch (header) {
    case "89504e47":
      return true; // PNG
    case "47494638":
      return true; // GIF
    case "ffd8ffe0":
    case "ffd8ffe1":
    case "ffd8ffe2":
    case "ffd8ffe3":
    case "ffd8ffe8":
      return true; // JPEG
    default:
      return false; // Not a known image type
  }
};

// PDF file reader function
export const readPDFFile = async (file) => {
  try {
    const pdfData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });

    // Check if the file is actually a PDF
    if (!isValidPDF(pdfData)) {
      return {
        data: file,
        error: "Invalid PDF file. Upload aborted.",
      };
    }

    return { error: null };
  } catch (error) {
    return {
      error: "An error occurred, aborting file upload.",
    };
  }
};

// Function to check if the file is a valid PDF
export const isValidPDF = (arrayBuffer) => {
  const arr = new Uint8Array(arrayBuffer).subarray(0, 5);
  const header = String.fromCharCode.apply(null, arr);
  return header === "%PDF-";
};
/**
 * Validates and reads a file based on specified criteria.
 * ALWAYS USE WITH AWAIT
 * @param {File} file - The file object to be validated and read.
 * @param {Array<string>} allowedTypes - Array of allowed file type categories ('image', 'pdf', 'excel').
 * @param {number} maxSizeInMB= - Maximum allowed file size in megabytes, pass 0 to skip this check
 * @returns {Promise<Object>} - A promise that resolves to the file data or an error object.
 */
export const validateAndReadFile = async (
  file,
  allowedTypes,
  maxSizeInMB = -1
) => {
  // Define allowed MIME types
  const allowedMimeTypes = {
    image: ["image/jpeg", "image/png", "image/jpg", "image/bmp", "image/gif"],
    pdf: ["application/pdf"],
    excel: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ],
  };

  // Convert MB to bytes
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  // Check if file was selected
  if (!file) {
    return { error: "Please select a file to upload!" };
  }

  /**
   * Checks if the file type is allowed.
   * @param {File} file - The file to check.
   * @param {Array<string>} types - Array of allowed type categories.
   * @returns {boolean} - Whether the file type is allowed.
   */
  const isAllowedFileType = (file, types) => {
    const allowedMimes = types.flatMap((type) => allowedMimeTypes[type] || []);
    return allowedMimes.includes(file.type);
  };

  // Check if the file type is allowed
  if (!isAllowedFileType(file, allowedTypes)) {
    const formattedTypes =
      allowedTypes.length > 1
        ? `${allowedTypes.slice(0, -1).join(", ")} or ${allowedTypes.slice(-1)}`
        : allowedTypes[0];

    return { error: `Please upload a ${formattedTypes} file!` };
  }

  // Check file size
  if (!(maxSizeInBytes <= 0) && file.size > maxSizeInBytes) {
    return { error: `Please upload a file less than ${maxSizeInMB}MB!` };
  }

  // Read file based on its type
  try {
    let result;
    if (file.type.startsWith("image/")) {
      result = await readImageFile(file);
    } else if (file.type === "application/pdf") {
      result = await readPDFFile(file);
    } else if (allowedMimeTypes.excel.includes(file.type)) {
      result = await readExcelFile(file);
    } else {
      return { error: "Unsupported file type!" };
    }

    return result;
  } catch (error) {
    return { error: error.message };
  }
};
